import { db } from '@/utils/firebaseConfig';
import {
  addDoc,
  collection,
  doc,
  updateDoc,
  deleteDoc,
  getDocs,
  getDoc,
  query,
  where
} from 'firebase/firestore';
import { Grupo, Participante } from '@/types/app';
import { logger } from '@/utils/logger';
import { validateGrupo, sanitizeString } from '@/utils/validation';

const buildParticipantesIds = (participantes: Array<{ id?: string | null }> | undefined): string[] => {
  if (!participantes || participantes.length === 0) return [];
  const ids = new Set<string>();
  for (const participante of participantes) {
    if (participante?.id && typeof participante.id === 'string') {
      const id = participante.id.trim();
      if (id) ids.add(id);
    }
  }
  return Array.from(ids);
};

export const grupoService = {
  createGrupo: async (grupoData: {
    nome: string;
    emoji: string;
    participantes: Participante[];
    incluirCriador: boolean;
    criadorId: string;
  }) => {
    try {
      if (!db) {
        throw new Error('Firebase não configurado neste build.');
      }
      // Validação dos dados do grupo
      const validation = validateGrupo(grupoData);
      if (!validation.isValid) {
        logger.warn('Invalid group data provided', { errors: validation.errors, grupoData });
        throw new Error(`Dados do grupo inválidos: ${validation.errors.join(', ')}`);
      }

      const sanitizedData = validation.sanitizedValue;

      // Validação adicional de segurança
      if (!sanitizedData.nome || !sanitizedData.criadorId) {
        throw new Error('Nome e criadorId são obrigatórios');
      }

      const participantes = sanitizedData.participantes || [];
      const docRef = await addDoc(collection(db, 'grupos'), {
        ...sanitizedData,
        participantes,
        participantesIds: buildParticipantesIds(participantes)
      });
      return { success: true, data: { id: docRef.id } };
    } catch (error: any) {
      logger.error('Error creating group in Firestore', {
        message: error.message,
        code: error.code,
        stack: error.stack
      });
      return { success: false, error: error.message };
    }
  },

  updateGrupo: async (grupoId: string, updates: Partial<Grupo>) => {
    try {
      if (!db) {
        throw new Error('Firebase não configurado neste build.');
      }
      // Validação do ID do grupo
      const sanitizedGrupoId = sanitizeString(grupoId);
      if (!sanitizedGrupoId) {
        throw new Error('ID do grupo inválido');
      }

      // Validação dos dados de atualização
      const validation = validateGrupo(updates, true);
      if (!validation.isValid) {
        logger.warn('Invalid group update data provided', { errors: validation.errors, updates });
        throw new Error(`Dados de atualização inválidos: ${validation.errors.join(', ')}`);
      }

      const grupoRef = doc(db, 'grupos', sanitizedGrupoId);

      // Usa os dados sanitizados
      const updatesLimpos = validation.sanitizedValue;

      // Trata cada campo especificamente
      if (updates.nome !== undefined) updatesLimpos.nome = updates.nome;
      if (updates.emoji !== undefined) updatesLimpos.emoji = updates.emoji;
      if (updates.participantes !== undefined) {
        const participantes = updates.participantes.map(p => ({
          id: p.id,
          nome: p.nome || '',
          email: p.email || '',
          foto: p.foto || '',
          chavePix: p.chavePix || null
        }));
        updatesLimpos.participantes = participantes;
        updatesLimpos.participantesIds = buildParticipantesIds(participantes);
      }
      if (updates.dataCriacao !== undefined) updatesLimpos.dataCriacao = updates.dataCriacao;
      if (updates.incluirCriador !== undefined) updatesLimpos.incluirCriador = updates.incluirCriador;

      await updateDoc(grupoRef, updatesLimpos);
    } catch (error) {
      logger.error('Error updating group', { error });
      throw error;
    }
  },

  deleteGrupo: async (grupoId: string) => {
    try {
      if (!db) {
        throw new Error('Firebase não configurado neste build.');
      }
      const grupoRef = doc(db, 'grupos', grupoId);
      await deleteDoc(grupoRef);
    } catch (error) {
      logger.error('Error deleting group', { error });
      throw error;
    }
  },

  getUserGrupos: async (userId: string) => {
    try {
      if (!db) {
        throw new Error('Firebase não configurado neste build.');
      }
      const gruposRef = collection(db, 'grupos');
      const q = query(gruposRef, where('criadorId', '==', userId));
      const snapshot = await getDocs(q);
      const data = snapshot.docs.map(doc => ({
        ...doc.data(),
        id: doc.id
      })) as Grupo[];
      return { success: true, data };
    } catch (error: any) {
      logger.error('Error fetching user groups', { error });
      return { success: false, error: error.message };
    }
  }
};
